import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiShoppingBag,
  FiBox,
  FiUsers,
  FiBarChart2,
  FiCreditCard,
  FiClipboard,
} from "react-icons/fi";

const IndustryRetail = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - Retail Industry Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Retail Industry | RedERP</title>
        <meta name="description" content="Explore how RedERP helps retail businesses thrive by streamlining sales, inventory, CRM, and reporting." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-10">
          <div className="flex-1 text-left">
            <h1 className="text-4xl font-bold mb-4">Retail, Reimagined with RedERP</h1>
            <p className="mb-6 text-sm sm:text-base">
              Whether you run a boutique, supermarket, or online store, RedERP gives you full control — from inventory and PoS to reporting and customer loyalty.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Get Started Now
            </a>
          </div>
          <div className="flex-1 hidden md:flex justify-end">
            <img
              src="/assets/images/retail-industry.png"
              alt="Retail Industry Illustration"
              className="w-[400px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold text-primary mb-3">Modules Tailored for Retail</h2>
          <p className="text-gray-600">
            RedERP combines the most essential tools for managing retail business in a single, intuitive platform.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left max-w-6xl mx-auto">
          {[
            {
              icon: <FiShoppingBag size={24} />,
              title: "Point of Sale",
              desc: "Run fast checkouts, apply discounts, manage promotions, and track cash flow at each terminal.",
            },
            {
              icon: <FiBox size={24} />,
              title: "Inventory & Stock",
              desc: "Automatically update inventory as you sell. Track batches, variants, reordering, and low-stock alerts.",
            },
            {
              icon: <FiUsers size={24} />,
              title: "Customer Relationship Management",
              desc: "Build customer loyalty with purchase history, follow-ups, feedback tracking, and targeted campaigns.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "BI & Reporting",
              desc: "View sales trends, top-selling products, profit margins, and real-time store KPIs — all in one dashboard.",
            },
            {
              icon: <FiCreditCard size={24} />,
              title: "Finance & Invoicing",
              desc: "Manage your cash flow, track expenses, generate invoices and monitor daily sales summaries.",
            },
            {
              icon: <FiClipboard size={24} />,
              title: "Procurement",
              desc: "Create purchase orders for vendors, handle stock intake, and optimize supplier performance.",
            },
          ].map((mod, index) => (
            <div key={index} className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center gap-3 text-primary mb-3">
                {mod.icon}
                <h3 className="font-bold">{mod.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{mod.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-10 py-16">
          <div className="flex-1 text-left">
            <h3 className="text-3xl font-bold mb-4">Run Your Store Smarter</h3>
            <p className="mb-6 text-sm sm:text-base">
              Start your 7-day free trial — no credit card required. Only $5/month for all modules and unlimited users.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Try RedERP for Retail
            </a>
          </div>
          <div className="flex-1 hidden md:flex justify-end">
            <img
              src="/assets/images/cta2.png"
              alt="Retail CTA"
              className="w-[360px] object-contain"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IndustryRetail;
